import React from 'react';
import { MODAL_STYLES, BUTTON_STYLES, selectChevron } from 'constants/styles';
import { getProteinUpgradeAmount, isProteinUpgradeChargeable } from 'utils/proteinPricing';
import { allergenConflictLabel } from 'utils/allergenLabels';
import ProteinSubstitutionSection from 'components/mealplan/substitution/ProteinSubstitutionSection';
import VeggieSubstitutionSection from 'components/mealplan/substitution/VeggieSubstitutionSection';
import StarchSubstitutionSection from 'components/mealplan/substitution/StarchSubstitutionSection';
import SauceSubstitutionSection from 'components/mealplan/substitution/SauceSubstitutionSection';
import GarnishSubstitutionSection from 'components/mealplan/substitution/GarnishSubstitutionSection';

const MealModal = ({
    meal,
    onClose,
    modalRef,
    saving,
    onProteinSubstitution,
    onVeggieSubstitution,
    onSauceSubstitution,
    onStarchSubstitution,
    onToggleGarnish,
    isLocked,
    showAddedBanner = false,
    billingChanges,
    deliveryDates,
    selectedDeliveryDate,
    showPrice = true,
    useALaCartePricing = false,
    onAddToCart,
    addToCartLabel = 'Add to Cart',
    isDraft = false,
    recipients = [],
    recipientId,
    onRecipientChange,
    onAddRecipient,
    // Storefront-only: [{ mealType, price }] for every portion the dish comes in.
    // MealPlanViewer's modal is always one meal type and passes none.
    mealTypeChoices = [],
    selectedMealType,
    onMealTypeChange,
}) => {
    const sectionLabelStyle = {
        display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 'bold', color: '#555',
    };

    const showMealTypePicker = mealTypeChoices.length > 1 && typeof onMealTypeChange === 'function';
    const activeMealType = selectedMealType || meal.meal || mealTypeChoices[0]?.mealType;

    const mealTypePicker = showMealTypePicker ? (
        <div>
            <label style={sectionLabelStyle}>Meal:</label>
            <div
                role="group"
                aria-label="Choose portion"
                style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}
            >
                {mealTypeChoices.map(({ mealType, price }) => {
                    const isActive = mealType === activeMealType;
                    const isDisabled = saving || isLocked;
                    return (
                        <button
                            key={mealType}
                            type="button"
                            aria-pressed={isActive}
                            onClick={() => { if (!isActive) onMealTypeChange(mealType); }}
                            disabled={isDisabled}
                            data-testid={`meal-type-option-${mealType}`}
                            style={BUTTON_STYLES.substitution(isActive, isDisabled)}
                        >
                            <span style={{ marginRight: '4px', fontSize: '12px' }}>
                                {isActive ? '✓' : '+'}
                            </span>
                            {mealType}
                            {price > 0 && ` (from $${Number(price).toFixed(2)})`}
                        </button>
                    );
                })}
            </div>
        </div>
    ) : null;

    const showRecipient = typeof onRecipientChange === 'function';
    const canAddRecipient = typeof onAddRecipient === 'function';
    const ADD_RECIPIENT_VALUE = '__add_recipient__';

    const recipientPicker = showRecipient ? (
        <div style={{ marginTop: '14px' }}>
            <label htmlFor="meal-modal-recipient" style={sectionLabelStyle}>
                Who is this meal for?
            </label>
            <select
                id="meal-modal-recipient"
                value={recipientId || ''}
                onChange={(e) => {
                    if (e.target.value === ADD_RECIPIENT_VALUE) {
                        onAddRecipient();
                        return;
                    }
                    onRecipientChange(e.target.value);
                }}
                disabled={saving || isLocked}
                style={{
                    width: '100%', maxWidth: '320px', padding: '10px 34px 10px 12px', fontSize: '14px',
                    border: '1px solid #d8d0bd', borderRadius: '8px', backgroundColor: '#fff', color: '#333',
                    ...selectChevron(),
                }}
            >
                {recipients.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                ))}
                {canAddRecipient && (
                    <option value={ADD_RECIPIENT_VALUE}>+ Add family member</option>
                )}
            </select>
        </div>
    ) : null;
    const renderActionBar = ({ compact } = {}) => (
        <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: compact ? '6px' : '10px',
            ...(compact ? {} : { marginTop: '18px', paddingTop: '16px', borderTop: '1px solid #eee' })
        }}>
            <button
                type="button"
                onClick={onClose}
                disabled={saving}
                style={{
                    padding: compact ? '5px 12px' : '11px 22px',
                    fontSize: compact ? '12px' : '14px',
                    fontWeight: '600',
                    borderRadius: compact ? '6px' : '8px',
                    border: '1.5px solid #ccc',
                    background: '#fff',
                    color: '#555',
                    cursor: saving ? 'not-allowed' : 'pointer'
                }}
            >
                Cancel
            </button>
            <button
                type="button"
                onClick={onAddToCart}
                disabled={saving || isLocked}
                data-testid="modal-add-to-cart"
                style={{
                    padding: compact ? '5px 14px' : '11px 26px',
                    fontSize: compact ? '12px' : '14px',
                    fontWeight: '700',
                    borderRadius: compact ? '6px' : '8px',
                    border: 'none',
                    background: '#f9a826',
                    color: '#fff',
                    cursor: (saving || isLocked) ? 'not-allowed' : 'pointer'
                }}
            >
                {addToCartLabel}
            </button>
        </div>
    );

    const allergenNames = allergenConflictLabel(meal.allergenConflicts);

    // Only surface auto-substitution badges while the dish is first being
    // configured (both surfaces pass true only for a draft) — reopening an
    // already-added dish shouldn't re-show them.
    const findAutoSub = (category) => showAddedBanner
        ? (meal.autoSubstitutions || []).find(s => s.category === category)
        : undefined;

    const proteinOptions = Array.isArray(meal.proteinOptions) ? meal.proteinOptions : [];
    const activeProtein = proteinOptions.find(p => p.isActive);
    const countedQuantity = isDraft ? 0 : Number(meal.quantity) || 0;
    // { from: true/false, amount: '20.99' } — kept separate (rather than one
    // formatted string) so "from" and the price can carry different typography.
    const displayPrice = (() => {
        if (meal.quantity > 0 && activeProtein) {
            const chargePremium = isProteinUpgradeChargeable({
                proteinOption: activeProtein,
                proteinOptions,
                mealType: meal.meal,
                billingChanges,
                deliveryDates,
                selectedDeliveryDate,
                useALaCarte: useALaCartePricing,
                countedQuantity,
            });
            const upgradeAmount = getProteinUpgradeAmount(activeProtein, useALaCartePricing);
            const total = Number(meal.price || 0) + (chargePremium ? upgradeAmount : 0);
            return { from: false, amount: total.toFixed(2) };
        }
        const hasPremiumOption = proteinOptions.length > 1 && proteinOptions.some(p => p.premium);
        return { from: hasPremiumOption, amount: Number(meal.price || 0).toFixed(2) };
    })();

    const actionBar = onAddToCart ? renderActionBar() : null;
    // Only mirror the buttons up top once the meal is actually in the cart
    // (i.e. there's something to edit/confirm without scrolling down).
    const topActionBar = onAddToCart && meal.quantity > 0 ? renderActionBar({ compact: true }) : null;

    return (
        <div
            className="meal-modal-container"
            style={MODAL_STYLES.overlay}
            onClick={onClose}
        >
            <div
                ref={modalRef}
                className='meal-modal'
                style={MODAL_STYLES.content}
                onClick={(e) => e.stopPropagation()}
            >

                {/* Storefront modal (onAddToCart) uses the Cancel button in the action
                    bar below to close; the plain × is only for the MealPlanViewer modal,
                    which has no action bar. */}
                {!onAddToCart && (
                    <button
                        onClick={onClose}
                        style={MODAL_STYLES.closeButton}
                        title="Cancel"
                    >
                        ×
                    </button>
                )}

                <div style={{ padding: '20px' }}>
                    {/* Top action bar — compact mirror of the one at the bottom so the
                        Add to Cart action is visible without scrolling. */}
                    {topActionBar && (
                        <div style={{ marginBottom: '10px' }}>
                            {topActionBar}
                        </div>
                    )}
                    <div
                        className="meal-modal-content"
                        style={{
                            display: 'flex',
                            gap: '20px',

                            alignItems: 'flex-start'
                        }}>
                        {/* Image section */}
                        <div
                            className="meal-modal-image"
                            style={{
                                flex: '0 0 300px',
                                minHeight: '200px'
                            }}>
                            {meal.imageUrl && (
                                <img
                                    src={meal.imageUrl}
                                    alt={meal.itemName}
                                    style={{
                                        width: '100%',
                                        height: '200px',
                                        objectFit: 'cover',
                                        borderRadius: '8px'
                                    }}
                                />
                            )}

                            {recipientPicker}
                        </div>

                        {/* Details section */}
                        <div
                            className="meal-modal-details"
                            style={{
                                flex: '1',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px'
                            }}>
                            {/* Title */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start'
                            }}>
                                <div>
                                    <h4 style={{ margin: '0 0 5px 0', color: '#333' }}>
                                        {meal.itemName}
                                        {meal.itemName.length > 30 && (
                                            <div />
                                        )}
                                        {(meal.quantity === 0 || isDraft) && (
                                            <span style={{
                                                width: 'fit-content',
                                                margin: '0 8px 8px 0',
                                                fontSize: '12px',
                                                backgroundColor: '#e3f2fd',
                                                color: '#1976d2',
                                                padding: '2px 6px',
                                                borderRadius: '3px'
                                            }}>
                                                {isDraft ? 'Not added yet' : 'Available to Add'}
                                            </span>
                                        )}
                                    </h4>
                                    {showPrice && meal.price > 0 && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'baseline',
                                            gap: '6px',
                                            margin: '0 0 10px 0'
                                        }}>
                                            {displayPrice.from && (
                                                <span style={{
                                                    fontSize: '13px',
                                                    color: '#333'
                                                }}>
                                                    from
                                                </span>
                                            )}
                                            <span style={{
                                                fontSize: '18px',
                                                fontWeight: '500',
                                                color: 'rgb(51, 51, 51)'
                                            }}>
                                                ${displayPrice.amount}
                                            </span>
                                        </div>
                                    )}
                                    {meal.quantity === 0 && (
                                        <div style={{
                                            backgroundColor: '#e3f2fd',
                                            border: '1px solid #bbdefb',
                                            borderRadius: '6px',
                                            padding: '12px',
                                            fontSize: '14px',
                                            color: '#1565c0'
                                        }}>
                                            <strong>Available Dish:</strong> This dish is available for this week but not currently in your meal plan. Click "Add" to add it to your orders!
                                        </div>
                                    )}

                                    {/* Whenever what's selected right now clashes with whoever
                                        this meal is for — a dish that couldn't be adjusted, a meal
                                        moved to someone else, an ingredient picked by hand. Shown
                                        as selected rather than half-stripped, so the customer
                                        decides on the real recipe. */}
                                    {meal.hasAllergenConflict && (
                                        <div style={{
                                            marginTop: '5px',
                                            backgroundColor: '#ffeaa7',
                                            border: '1px solid #fdcb6e',
                                            borderRadius: '6px',
                                            padding: '5px',
                                            fontSize: '12px',
                                            color: '#a1651b'
                                        }}>
                                            ⚠️ Allergy detected<strong>{allergenNames ? `: ${allergenNames}` : ' in this meal'}.</strong>{' '}
                                        </div>
                                    )}

                                </div>
                            </div>

                            {/* Above the substitutions: the portion decides which of them are offered. */}
                            {mealTypePicker}

                            {/* Show substitution options for ordered dishes only */}
                            {meal.quantity > 0 && (

                                <>
                                    {meal.proteinOptions.length > 0 && (
                                        <ProteinSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onProteinSubstitution={onProteinSubstitution}
                                            isLocked={isLocked}
                                            saving={saving}
                                            billingChanges={billingChanges}
                                            deliveryDates={deliveryDates}
                                            selectedDeliveryDate={selectedDeliveryDate}
                                            autoSubstitution={findAutoSub('protein')}
                                            useALaCartePricing={useALaCartePricing}
                                            countedQuantity={countedQuantity}
                                        />)}

                                    {meal.veggieOptions.length > 0 && (
                                        <VeggieSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onVeggieSubstitution={onVeggieSubstitution}
                                            isLocked={isLocked}
                                            saving={saving}
                                            autoSubstitution={findAutoSub('veggie')}
                                        />
                                    )}
                                    {meal.starchOptions.length > 0 && (
                                        <>
                                            <StarchSubstitutionSection
                                                order={meal}
                                                orderIndex={meal.actualIndex}
                                                onStarchSubstitution={onStarchSubstitution}
                                                isLocked={isLocked}
                                                saving={saving}
                                                autoSubstitution={findAutoSub('starch')}
                                            />
                                        </>
                                    )}
                                    {meal.sauceOptions.length > 0 && (
                                        <SauceSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onSauceSubstitution={onSauceSubstitution}
                                            isLocked={isLocked}
                                            saving={saving}
                                            autoSubstitution={findAutoSub('sauce')}
                                        />
                                    )}
                                    {meal.garnishOptions.length > 0 && (
                                        <GarnishSubstitutionSection
                                            order={meal}
                                            orderIndex={meal.actualIndex}
                                            onToggleGarnish={onToggleGarnish}
                                            isLocked={isLocked}
                                            saving={saving}
                                            autoSubstitution={findAutoSub('garnish')}
                                        />
                                    )}


                                    {/* Ingredients section for snacks */}
                                    {/* {meal.meal === 'Snack' && (meal.originalIngredients && meal.originalIngredients.length === 1) && (
                                        <div>
                                            <label style={{
                                                display: 'block',
                                                marginBottom: '8px',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                color: '#555'
                                            }}>
                                                All Ingredients:
                                            </label>
                                            <div
                                                className="ingredient-buttons"
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: '8px'
                                                }}>
                                                {meal.originalIngredients.map((ingredient, ingredientIndex) => {
                                                    const isActive = meal.finalIngredients.includes(ingredient.id);
                                                    return (
                                                        <button
                                                            key={ingredientIndex}
                                                            className="ingredient-button"
                                                            onClick={() => onToggleIngredient(meal.actualIndex, ingredient.name, isActive)}
                                                            disabled={saving}
                                                            style={getIngredientButtonStyle(isActive, saving)}
                                                            title={isActive ? `Remove ${ingredient.name}` : `Add ${ingredient.name}`}
                                                        >
                                                            <span style={{
                                                                marginRight: '4px',
                                                                fontSize: '12px'
                                                            }}>
                                                                {isActive ? '✓' : '+'}
                                                            </span>
                                                            {ingredient.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )} */}
                                </>
                            )}

                            {/* Show ingredients preview for available dishes */}
                            {meal.quantity === 0 && (
                                <div>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: '8px',
                                        fontSize: '14px',
                                        fontWeight: 'bold',
                                        color: '#555'
                                    }}>
                                        What's included:
                                    </label>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        lineHeight: '1.6'
                                    }}>
                                        {(() => {
                                            const categories = [
                                                { label: 'Protein', options: meal.proteinOptions, showSubs: true },
                                                { label: 'Starch', options: meal.starchOptions, showSubs: true },
                                                { label: 'Vegetables', options: meal.veggieOptions, showSubs: false },
                                                { label: 'Sauce', options: meal.sauceOptions, showSubs: true },
                                                { label: 'Garnish', options: meal.garnishOptions, showSubs: false },
                                            ];
                                            const originalIds = new Set((meal.originalIngredients || []).map(i => i.id));
                                            const lines = categories
                                                .map(({ label, options, showSubs }) => {
                                                    if (!options || !Array.isArray(options)) return null;
                                                    const display = options.filter(o => originalIds.has(o.id));
                                                    if (display.length === 0) return null;
                                                    const stripSF = (s) => (s || '').replace(/^SF /, '');
                                                    const namesList = display.map(o => {
                                                        const cleanSubs = (o.subIngredientNames || []).map(stripSF);
                                                        const subs = showSubs && cleanSubs.length > 0
                                                            ? ` (${cleanSubs.join(', ')})`
                                                            : '';
                                                        return { name: stripSF(o.name?.trim()), subs, rawSubs: cleanSubs };
                                                    });
                                                    return { label, namesList };
                                                })
                                                .filter(Boolean);

                                            if (lines.length === 0) {
                                                return meal.originalIngredients?.length > 0
                                                    ? meal.originalIngredients.map(i => i.name.trim()).join(', ')
                                                    : 'Original Ingredients Not Listed';
                                            }

                                            const singleCategory = lines.length === 1;
                                            if (singleCategory) {
                                                return lines[0].namesList.map((item, i) => {
                                                    const nameL = item.name?.toLowerCase() || '';
                                                    const titleL = meal.itemName?.toLowerCase() || '';
                                                    const sameName = nameL === titleL || titleL.includes(nameL) || nameL.includes(titleL);
                                                    const subsText = item.subs || (item.rawSubs.length > 0 ? ` (${item.rawSubs.join(', ')})` : '');
                                                    if (sameName && !subsText) {
                                                        return null;
                                                    }
                                                    return <div key={i}><span style={{ fontWeight: '600' }}>{item.name}</span>{subsText}</div>;
                                                });
                                            }
                                            return lines.map((line, i) => (
                                                <div key={i}>
                                                    <span style={{ fontWeight: '600', bold: 'true' }}>{line.label}:</span>{' '}
                                                    {line.namesList.map((item, j) => (
                                                        <span key={j}>
                                                            {item.name}{item.subs}{j < line.namesList.length - 1 ? ', ' : ''}
                                                        </span>
                                                    ))}
                                                </div>
                                            ));
                                        })()}
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom action bar */}
                    {actionBar}
                </div>

            </div>
        </div >
    );
};

export default MealModal;
